import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  ArrowRight,
  Search,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  DollarSign,
  Mail,
  Shield,
  UserPlus,
  Settings,
  AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import Footer from "../components/Footer";

export default function FAQPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [openItems, setOpenItems] = useState({});
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = [
    { id: "all", name: "All Questions", icon: HelpCircle },
    { id: "getting-started", name: "Getting Started", icon: UserPlus },
    { id: "tasks", name: "Tasks & Earnings", icon: Mail },
    { id: "payments", name: "Payments", icon: DollarSign },
    { id: "account", name: "Account & Security", icon: Shield },
    { id: "troubleshooting", name: "Troubleshooting", icon: Settings }
  ];

  const faqs = [
    // Getting Started
    {
      category: "getting-started",
      question: "What is GigsEasy and how does it work?",
      answer: "GigsEasy is a platform that connects people with simple online tasks. You sign up, register your email addresses, receive tasks via email, complete them, and earn points that convert to real money. It's designed to be simple enough that anyone can do it from anywhere with an internet connection."
    },
    {
      category: "getting-started",
      question: "Is GigsEasy free to join?",
      answer: "Yes, absolutely! GigsEasy is 100% free to join. We never charge any fees to our members. You earn money - you don't pay us anything."
    },
    {
      category: "getting-started",
      question: "Who can join GigsEasy?",
      answer: "Anyone 18 years or older can join GigsEasy. We accept members from most countries worldwide. You just need a valid email address and a social media profile for verification."
    },
    {
      category: "getting-started",
      question: "Why do you need my social media profile?",
      answer: "We use social media verification to prevent fraud and ensure our platform is used by real people. This helps maintain task quality and protects our community. We don't post anything to your social media or access your personal data beyond basic verification."
    },
    {
      category: "getting-started",
      question: "How long does account approval take?",
      answer: "Most accounts are approved within 24-48 hours. During high-demand periods, it may take slightly longer. You'll receive an email notification once your account is approved and ready to use."
    },
    
    // Tasks & Earnings
    {
      category: "tasks",
      question: "What types of tasks are available?",
      answer: "We offer several task types: Click Tasks (click a link - takes seconds), Email Reply Tasks (reply with specific content), Survey Tasks (complete short surveys), and Bonus Tasks (special high-value opportunities). Task availability varies based on your location and profile."
    },
    {
      category: "tasks",
      question: "How many points can I earn per task?",
      answer: "Points vary by task type and complexity. Click tasks typically earn 10-50 points, email replies earn 25-100 points, surveys earn 50-200 points, and bonus tasks can earn 100-500+ points. Remember, 100 points = $1 USD."
    },
    {
      category: "tasks",
      question: "Why am I not receiving many tasks?",
      answer: "Task availability depends on several factors: number of registered emails (more emails = more tasks), your location, profile completeness, and overall task availability. Make sure you've registered multiple emails and completed your profile fully. Also check your spam folder!"
    },
    {
      category: "tasks",
      question: "Can I register multiple email addresses?",
      answer: "Yes! You can register up to 10 email addresses to receive tasks. More email addresses means more task opportunities and higher earning potential. We recommend using Gmail addresses for best deliverability."
    },
    {
      category: "tasks",
      question: "What happens if I don't complete a task?",
      answer: "If you don't complete a task, you simply don't earn points for it. There's no penalty, but consistently ignoring tasks may reduce your priority for future task distribution. We recommend completing tasks promptly when you receive them."
    },
    {
      category: "tasks",
      question: "Do tasks expire?",
      answer: "Some tasks have time limits, especially popular ones with limited spots. We recommend completing tasks as soon as possible after receiving them. Tasks with deadlines will clearly show the expiration time."
    },
    
    // Payments
    {
      category: "payments",
      question: "How do I get paid?",
      answer: "We currently offer PayPal as our primary payment method. Once you reach the minimum withdrawal amount ($7.00), you can request a withdrawal from your dashboard. Payments are typically processed within 1-3 business days."
    },
    {
      category: "payments",
      question: "What is the minimum withdrawal amount?",
      answer: "The minimum withdrawal amount is $7.00 USD (700 points). There's no maximum limit - you can withdraw your entire balance at once if you wish."
    },
    {
      category: "payments",
      question: "Are there any fees for withdrawals?",
      answer: "GigsEasy does not charge any withdrawal fees. However, PayPal may have their own fees depending on your country and account type. Check PayPal's fee structure for details."
    },
    {
      category: "payments",
      question: "How long do withdrawals take?",
      answer: "Withdrawal requests are typically reviewed within 24 hours. Once approved, PayPal payments are processed within 1-3 business days. Total time from request to receiving funds is usually 2-4 business days."
    },
    {
      category: "payments",
      question: "Do my points expire?",
      answer: "No, your points never expire! You can accumulate them over time and withdraw whenever you're ready. There's no rush - your earnings are safe in your account."
    },
    {
      category: "payments",
      question: "Can I use a different PayPal email than my GigsEasy email?",
      answer: "Yes! You can set any PayPal email address in your profile settings. It doesn't need to match your GigsEasy account email. Just make sure it's connected to a valid, active PayPal account."
    },
    {
      category: "payments",
      question: "Is my income from GigsEasy taxable?",
      answer: "Earnings from GigsEasy may be considered taxable income depending on your country's laws. You are responsible for reporting your earnings and paying any applicable taxes. We recommend consulting a tax professional for advice specific to your situation."
    },
    
    // Account & Security
    {
      category: "account",
      question: "How do I change my password?",
      answer: "You can change your password from your Profile page. Click on your profile, look for the security settings, and follow the prompts to update your password. We recommend using a strong, unique password."
    },
    {
      category: "account",
      question: "Is my personal information safe?",
      answer: "Yes, we take security seriously. We use industry-standard encryption, secure payment processing, and never share your personal information with third parties without your consent. Read our Privacy Policy for full details."
    },
    {
      category: "account",
      question: "Can I delete my account?",
      answer: "Yes, you can request account deletion from your Profile page under 'Data & Account Management'. This will permanently delete your account and all associated data. Make sure to withdraw any remaining balance first, as deletion cannot be undone."
    },
    {
      category: "account",
      question: "What should I do if I forget my password?",
      answer: "Click 'Forgot Password' on the login page and enter your email address. We'll send you a password reset link. If you don't receive it within a few minutes, check your spam folder."
    },
    {
      category: "account",
      question: "Can I have multiple GigsEasy accounts?",
      answer: "No, each person is only allowed one GigsEasy account. Multiple accounts are against our Terms of Service and will result in all accounts being suspended. You can register multiple emails within your single account."
    },
    
    // Troubleshooting
    {
      category: "troubleshooting",
      question: "I'm not receiving task emails. What should I do?",
      answer: "First, check your spam/junk folder and mark GigsEasy emails as 'Not Spam'. Add our email domains to your contacts. Make sure your email addresses are verified in your dashboard. If problems persist, try adding a Gmail account as they have the best deliverability."
    },
    {
      category: "troubleshooting",
      question: "My task didn't register as completed. What happened?",
      answer: "Some tasks take a few minutes to verify completion. Wait 5-10 minutes and check again. If it still shows incomplete, make sure you followed all instructions exactly. For click tasks, ensure you visited the full page (not just clicked and closed). Contact support if issues persist."
    },
    {
      category: "troubleshooting",
      question: "My withdrawal is taking longer than expected. Why?",
      answer: "Withdrawals typically process within 1-3 business days. Delays can occur due to: verification requirements for new accounts, PayPal processing times, weekends/holidays, or high volume periods. If it's been more than 5 business days, contact our support team."
    },
    {
      category: "troubleshooting",
      question: "I can't log into my account. What should I do?",
      answer: "Try these steps: 1) Clear your browser cache and cookies, 2) Try a different browser, 3) Use the 'Forgot Password' feature to reset your password, 4) Make sure you're using the correct email address. If none of these work, contact support."
    },
    {
      category: "troubleshooting",
      question: "The website isn't loading properly. How can I fix this?",
      answer: "Try refreshing the page, clearing your browser cache, or using a different browser. Make sure your internet connection is stable. If problems persist, the issue may be temporary - try again in a few minutes. Our systems are monitored 24/7."
    }
  ];

  const toggleItem = (index) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = searchQuery === "" || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === "all" || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <Link to="/register">
            <Button className="bg-[#00B894] hover:bg-[#00A085] text-white">
              Start Earning Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-white via-[#00B894]/5 to-[#0984E3]/5">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#00B894]/10 text-[#00B894] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <HelpCircle className="w-4 h-4" />
            Find answers quickly
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Frequently Asked{" "}
            <span className="bg-gradient-to-r from-[#00B894] to-[#0984E3] bg-clip-text text-transparent">
              Questions
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Everything you need to know about GigsEasy. Can't find what you're looking for? 
            Contact our support team for help.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 text-lg border-2 border-slate-200 focus:border-[#00B894] rounded-xl"
            />
          </div>
        </div>
      </section>

      {/* Category Pills */}
      <section className="py-8 px-6 bg-slate-50 border-b border-slate-200 sticky top-[73px] z-40">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category.id
                    ? 'bg-[#00B894] text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                <category.icon className="w-4 h-4" />
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No questions found matching your search.</p>
              <button 
                onClick={() => { setSearchQuery(""); setActiveCategory("all"); }}
                className="text-[#00B894] hover:underline mt-2"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => {
                const originalIndex = faqs.indexOf(faq);
                const isOpen = openItems[originalIndex];
                const CategoryIcon = categories.find(c => c.id === faq.category)?.icon || HelpCircle;
                
                return (
                  <div 
                    key={originalIndex}
                    className={`border-2 rounded-2xl transition-all ${
                      isOpen ? 'border-[#00B894] bg-[#00B894]/5' : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <button
                      onClick={() => toggleItem(originalIndex)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isOpen ? 'bg-[#00B894] text-white' : 'bg-slate-100 text-slate-500'
                        }`}>
                          <CategoryIcon className="w-5 h-5" />
                        </div>
                        <span className={`font-semibold ${isOpen ? 'text-[#00B894]' : 'text-slate-900'}`}>
                          {faq.question}
                        </span>
                      </div>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-[#00B894] flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      )}
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-5">
                        <div className="pl-14">
                          <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 px-6 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">
            Still Have Questions?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Can't find the answer you're looking for? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact-us">
              <Button size="lg" className="bg-[#00B894] hover:bg-[#00A085] text-white px-8">
                Contact Support
              </Button>
            </Link>
            <Link to="/how-to-earn">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Read Getting Started Guide
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Popular Resources
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/how-to-earn" className="bg-slate-50 rounded-2xl p-6 hover:bg-slate-100 transition-colors group">
              <UserPlus className="w-10 h-10 text-[#00B894] mb-4" />
              <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-[#00B894]">How to Earn Money</h3>
              <p className="text-slate-600 text-sm">Step-by-step guide to getting started and earning your first dollars.</p>
            </Link>
            <Link to="/maximize-earnings" className="bg-slate-50 rounded-2xl p-6 hover:bg-slate-100 transition-colors group">
              <DollarSign className="w-10 h-10 text-[#00B894] mb-4" />
              <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-[#00B894]">Maximize Your Earnings</h3>
              <p className="text-slate-600 text-sm">Pro tips from top earners to boost your income.</p>
            </Link>
            <Link to="/points-payments" className="bg-slate-50 rounded-2xl p-6 hover:bg-slate-100 transition-colors group">
              <Settings className="w-10 h-10 text-[#00B894] mb-4" />
              <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-[#00B894]">Points & Payments</h3>
              <p className="text-slate-600 text-sm">Understanding how points work and getting paid.</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
